<template>
  <div class="date-filter">
    <filter-card-wrapper :title="title" :expanded="expanded">
      <div slot="body">
        <div class="date-filter__head">
          <div class="date-filter__radio-area">
            <form-switch
              :id="getSwitchId"
              :checked="isRelativeFilter"
              label="Relative Date"
              @onClick="handleRelativeRadioChange" />
          </div>
          <form-select
            id="date-filter-type"
            :options="relativeDateOptions"
            :required="true"
            :value="rangeType"
            @input="onDateTypeChange($event)" />
        </div>
        <div v-if="isRelativeFilter" class="date-filter__date-pickers">
          <date-picker
            id="from-date-filter"
            ref="from-date-filter"
            name="from-date-filter"
            label="From Date"
            placeholder="Choose from Date"
            format="DD-MMM-YYYY"
            :value="fromDayFormatted"
            :formatter="relativeDateComputed"
            :error-message="validationError.fromDate"
            :clearable="true"
            @pick="handlePick($event, 'fromDay')"
            @input="handleClear($event, 'fromDay')" />
          <date-picker
            v-if="rangeType.value === 'range'"
            id="to-date-filter"
            ref="to-date-filter"
            name="to-date-filter"
            label="To Date"
            placeholder="Choose to Date"
            format="DD-MMM-YYYY"
            :value="toDayFormatted"
            :formatter="relativeDateComputed"
            :error-message="validationError.toDate"
            :clearable="true"
            :disabled="!fromDayFormatted"
            :min-date="fromDayFormatted"
            @pick="handlePick($event, 'toDay')"
            @input="handleClear($event, 'toDay')" />
        </div>
        <div v-else class="date-filter__date-pickers">
          <date-input
            id="from-date-filter"
            ref="from-date-filter"
            name="from-date-filter"
            label="From Date"
            placeholder="Choose from Date"
            date-format="DD-MMM-YYYY"
            :value="fromDateLocal"
            :clearable="true"
            @input="handlePick($event, 'from')"
            @clear="handleClear($event, 'from')" />
          <date-input
            v-if="rangeType.value === 'range'"
            id="to-date-filter"
            ref="to-date-filter"
            name="to-date-filter"
            label="To Date"
            placeholder="Choose to Date"
            date-format="DD-MMM-YYYY"
            :value="toDateLocal"
            :disabled="!fromDateLocal"
            :clearable="true"
            :min-date="fromDateLocal"
            @input="handlePick($event, 'to')"
            @clear="handleClear($event, 'to')" />
        </div>
        <Button
          label="Today"
          appearance="tertiary"
          class="date-filter__button"
          @onClick="onPresetDateChange('Today')" />
        <Button
          v-if="rangeType.value === 'range'"
          label="Last 7 days"
          appearance="tertiary"
          class="date-filter__button"
          @onClick="onPresetDateChange('Last 7 days')" />
        <Button
          v-if="rangeType.value === 'range'"
          label="Last 30 days"
          appearance="tertiary"
          class="date-filter__button no-margin"
          @onClick="onPresetDateChange('Last 30 days')" />
      </div>
    </filter-card-wrapper>
  </div>
</template>

<script>
// External Components
import {
  differenceInCalendarDays,
  startOfToday,
  subWeeks,
  subDays,
  endOfDay,
} from "date-fns";

// Custom Component
import {
  formatToISOString,
  formatDateFromGivenDay,
  formatToRelativeDate,
} from "@/utils/helpers";
import FilterCardWrapper from "./FilterCardWrapper";
import { relativeDateOptions } from "@/config/filterDataConstant";

export default {
  name: "DateFilters",
  components: {
    FilterCardWrapper,
  },
  props: {
    expanded: { type: Boolean, default: false },
    isSearchable: { type: Boolean, default: false },
    title: { type: String, required: true },
    fieldData: { type: Object, required: true },
    fromDate: { type: String, default: null },
    toDate: { type: String, default: null },
    relativeDate: { type: String, default: null },
    fromDays: { type: [Number, String], default: null },
    toDays: { type: [Number, String], default: null },
    filterType: { type: String, default: null },
  },
  data() {
    let fromDateLocal = "";
    let toDateLocal = "";
    let fromDaysLocal = "";
    let toDaysLocal = "";
    let fromDayFormatted = "";
    let toDayFormatted = "";
    let relativeDateLocal = "";

    if (this.filterType === "RELATIVE_DATE_RANGE") {
      fromDaysLocal = this.fromDays;
      toDaysLocal = this.toDays;
      relativeDateLocal = startOfToday();
      fromDateLocal = null;
      toDateLocal = null;
      fromDayFormatted = formatDateFromGivenDay(
        fromDaysLocal,
        relativeDateLocal
      );
      toDayFormatted = formatDateFromGivenDay(toDaysLocal, relativeDateLocal);
    } else {
      fromDateLocal = this.fromDate ? new Date(this.fromDate) : null;
      toDateLocal = this.toDate
        ? new Date(this.toDate)
        : this.fromDate
        ? endOfDay(this.fromDate)
        : null;
      relativeDateLocal = null;
      fromDaysLocal = null;
      toDaysLocal = null;
      fromDayFormatted = null;
      toDayFormatted = null;
    }

    const rangeType =
      toDateLocal || toDayFormatted
        ? {
            label: "RANGE",
            value: "range",
          }
        : {
            label: "Exact",
            value: "exact",
          };

    return {
      validationError: {
        fromDate: "Enter valid From date",
        toDate: "Enter valid To date",
      },
      relativeDateOptions,
      isRelativeFilter: this.filterType === "RELATIVE_DATE_RANGE",
      fromDateLocal,
      toDateLocal,
      fromDaysLocal,
      toDaysLocal,
      fromDayFormatted,
      toDayFormatted,
      relativeDateLocal,
      rangeType,
    };
  },
  computed: {
    relativeDateComputed() {
      return {
        stringify: (date) => formatToRelativeDate(date, this.relativeDateLocal),
      };
    },
    getSwitchId() {
      return (this.title || "").split(" ").join("").toLowerCase();
    },
  },
  watch: {
    filterType() {
      this.isRelativeFilter = this.filterType === "RELATIVE_DATE_RANGE";
    },
    fromDate() {
      this.fromDateLocal = this.fromDate ? new Date(this.fromDate) : "";
    },
    toDate() {
      this.toDateLocal = this.toDate ? new Date(this.toDate) : "";
    },
    relativeDate() {
      this.relativeDateLocal = this.relativeDate ? startOfToday() : "";
    },
    fromDays() {
      this.fromDaysLocal = this.fromDays;
      const relativeDateLocal = this.relativeDate ? startOfToday() : "";
      this.fromDayFormatted = formatDateFromGivenDay(
        this.fromDaysLocal,
        relativeDateLocal
      );
    },
    toDays() {
      this.toDaysLocal = this.toDays;
      const relativeDateLocal = this.relativeDate ? startOfToday() : "";
      this.toDayFormatted = formatDateFromGivenDay(
        this.toDaysLocal,
        relativeDateLocal
      );
    },
  },
  methods: {
    handlePick(event, type) {
      if (type === "from") {
        this.fromDateLocal = event;
        if (this.rangeType.value === "exact") {
          this.toDateLocal = endOfDay(event);
        } else {
          this.toDateLocal = null;
        }
      } else if (type === "to") {
        this.toDateLocal = endOfDay(event);
      } else if (type === "fromDay") {
        this.fromDayFormatted = event;
        this.relativeDateLocal = startOfToday();
        const fromDaysLocal = differenceInCalendarDays(
          event,
          this.relativeDateLocal
        );
        this.fromDaysLocal = fromDaysLocal;
        if (this.rangeType.value === "exact") {
          this.toDaysLocal = fromDaysLocal;
          this.toDateLocal = endOfDay(event);
        } else {
          this.toDaysLocal = null;
          this.toDateLocal = null;
        }
      } else if (type === "toDay") {
        this.toDayFormatted = event;
        this.relativeDateLocal = startOfToday();
        this.fromDaysLocal = differenceInCalendarDays(
          this.fromDayFormatted,
          this.relativeDateLocal
        );
        this.toDaysLocal = differenceInCalendarDays(
          event,
          this.relativeDateLocal
        );
      }
      this.onSubmit();
    },
    handleClear(event, type) {
      if (!event) {
        if (type === "from") {
          this.fromDateLocal = null;
          this.toDateLocal = null;
        } else if (type === "to") {
          this.toDateLocal = null;
        } else if (type === "fromDay") {
          this.fromDaysLocal = null;
          this.fromDayFormatted = null;
          this.toDaysLocal = null;
          this.toDayFormatted = null;
          this.relativeDateLocal = null;
        } else if (type === "toDay") {
          this.toDaysLocal = null;
          this.toDayFormatted = null;
        }
        this.onSubmit();
      }
    },
    handleRelativeRadioChange(enabled) {
      this.isRelativeFilter = enabled;
      if (enabled) {
        if (!this.relativeDateLocal) {
          this.relativeDateLocal = startOfToday();
        }
        this.fromDaysLocal = this.fromDateLocal
          ? differenceInCalendarDays(this.fromDateLocal, this.relativeDateLocal)
          : null;
        this.toDaysLocal = this.toDateLocal
          ? differenceInCalendarDays(this.toDateLocal, this.relativeDateLocal)
          : null;
        this.fromDayFormatted = formatDateFromGivenDay(
          this.fromDaysLocal,
          this.relativeDateLocal
        );
        this.toDayFormatted = formatDateFromGivenDay(
          this.toDaysLocal,
          this.relativeDateLocal
        );
        this.fromDateLocal = null;
        this.toDateLocal = null;
      } else {
        const relativeDateLocal = this.relativeDateLocal || startOfToday();
        this.fromDateLocal = formatDateFromGivenDay(
          this.fromDaysLocal,
          relativeDateLocal
        );
        this.toDateLocal = endOfDay(
          formatDateFromGivenDay(this.toDaysLocal, relativeDateLocal)
        );
        this.fromDaysLocal = null;
        this.toDaysLocal = null;
        this.fromDayFormatted = null;
        this.toDayFormatted = null;
        this.relativeDateLocal = null;
      }
      this.onSubmit();
    },
    onPresetDateChange(presetType) {
      const today = startOfToday();
      if (presetType === "Today") {
        if (this.isRelativeFilter) {
          this.relativeDateLocal = today;
          this.fromDaysLocal = differenceInCalendarDays(
            today,
            this.relativeDateLocal
          );
          this.fromDayFormatted = today;
          this.toDayFormatted = today;
          this.toDaysLocal = differenceInCalendarDays(
            today,
            this.relativeDateLocal
          );
          this.fromDateLocal = null;
          this.toDateLocal = null;
        } else {
          this.fromDateLocal = today;
          this.toDateLocal = endOfDay(today);
          this.fromDaysLocal = null;
          this.toDaysLocal = null;
          this.fromDayFormatted = null;
          this.toDayFormatted = null;
          this.relativeDateLocal = null;
        }
      } else if (presetType === "Last 7 days") {
        const fromDate = subWeeks(today, 1);
        if (this.isRelativeFilter) {
          this.relativeDateLocal = today;
          this.fromDaysLocal = differenceInCalendarDays(
            fromDate,
            this.relativeDateLocal
          );
          this.fromDayFormatted = fromDate;
          this.toDaysLocal = differenceInCalendarDays(
            today,
            this.relativeDateLocal
          );
          this.toDayFormatted = today;
          this.fromDateLocal = null;
          this.toDateLocal = null;
        } else {
          this.fromDateLocal = fromDate;
          this.toDateLocal = endOfDay(today);
          this.fromDaysLocal = null;
          this.toDaysLocal = null;
          this.fromDayFormatted = null;
          this.toDayFormatted = null;
          this.relativeDateLocal = null;
        }
      } else if (presetType === "Last 30 days") {
        const fromDate = subDays(today, 30);
        if (this.isRelativeFilter) {
          this.relativeDateLocal = today;
          this.fromDaysLocal = differenceInCalendarDays(
            fromDate,
            this.relativeDateLocal
          );
          this.fromDayFormatted = fromDate;
          this.toDaysLocal = differenceInCalendarDays(
            today,
            this.relativeDateLocal
          );
          this.toDayFormatted = today;
          this.fromDateLocal = null;
          this.toDateLocal = null;
        } else {
          this.fromDateLocal = fromDate;
          this.toDateLocal = endOfDay(today);
          this.fromDaysLocal = null;
          this.toDaysLocal = null;
          this.fromDayFormatted = null;
          this.toDayFormatted = null;
          this.relativeDateLocal = null;
        }
      }
      this.onSubmit();
    },
    onDateTypeChange(option) {
      this.rangeType = option;
      if (option.value === "exact") {
        this.toDaysLocal = null;
        // this.toDateLocal = null;
        this.toDayFormatted = null;
        this.onSubmit();
      }
    },
    onSubmit() {
      this.$parent.$listeners.onDateSelect(
        this.fromDateLocal || this.fromDayFormatted
          ? [
              {
                from: this.fromDateLocal
                  ? formatToISOString(this.fromDateLocal)
                  : null,
                to: this.toDateLocal
                  ? formatToISOString(this.toDateLocal)
                  : null,
                from_days: this.fromDaysLocal,
                to_days: this.toDaysLocal,
                relative_date: this.relativeDateLocal
                  ? formatToISOString(this.relativeDateLocal)
                  : null,
                type: this.relativeDateLocal ? "RELATIVE_DATE_RANGE" : "RANGE",
                inclusive_from: !!(this.fromDateLocal || this.fromDayFormatted),
                inclusive_to: !!(this.toDateLocal || this.toDayFormatted),
                filterName: this.title,
                property_id: this.fieldData.fieldName,
              },
            ]
          : "",
        this.title
      );
    },
  },
};
</script>

<style lang="scss">
.date-filter {
  display: flex;
  & .filter-card-wrapper__body {
    margin-top: 16px;
  }
  & .filter-card-wrapper__body .form-group {
    margin-bottom: 16px;
  }
  & .date-filter__button {
    display: flex;
    margin-bottom: 16px;
    justify-content: center;
    &.no-margin {
      margin: 0;
    }
  }
  &__head {
    display: flex;
    margin-bottom: 16px;
    justify-content: space-between;
    flex: 1;
    align-items: baseline;
    & .form-input__field {
      width: 120px;
    }
  }
  &__date-pickers {
    display: flex;
    & #from-date-filter {
      margin-right: 8px;
    }
    & #to-date-filter {
      margin-top: 0px;
    }
  }
}
.mx-datepicker {
  width: 100% !important;
}
</style>

<style lang="scss">
.date-filter__radio-area {
  display: flex;
  align-items: center;
  & .radio {
    position: relative;
    left: -10px;
  }
  & span {
    margin-left: 8px;
  }
}
</style>
